<script setup>
import { gsap } from "gsap";
import eventBy from "../../assets/gql/eventBy.gql";

const route = useRoute();
const { locale, t } = useI18n();
const isModalOpen = ref(false);

const variables = {
  limit: 5,
  slug: route?.params?.id,
  language: locale?.value?.toString().toUpperCase(),
};

const { data } = await useAsyncQuery(eventBy, variables);

if (data?.value?.eventBy?.translation && route?.params?.id != data?.value?.eventBy?.translation?.slug) {
  const correctUrl = route.fullPath.replace(
		/[^/]+\/?$/,
		`${data?.value?.eventBy?.translation?.slug}/`
	);

  await navigateTo(correctUrl, {
    redirectCode: 301,
  });
}

if (!data?.value?.eventBy || !data?.value?.eventBy?.translation) {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found" });
}

const translation = data?.value?.eventBy?.translation;
const hasEn = data?.value?.eventBy?.englishTranslation ? true : false;
const csSlug = data?.value?.eventBy?.czechTranslation?.slug ?? null;
const enSlug = data?.value?.eventBy?.englishTranslation?.slug ?? null;

function replaceBlockquotesAndRemoveStyles(htmlContent) {
  // Create a DOM parser to parse the HTML content

  if (!htmlContent) return;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  // Remove styles from all elements
  const allElements = doc.querySelectorAll("*");
  allElements.forEach((element) => {
    element.removeAttribute("style");
  });

  // Find all blockquote elements
  // Replace each blockquote element with the custom div structure
  const pres = doc.querySelectorAll("pre");
  pres.forEach((pre) => {
    const content = pre.innerHTML;
    pre.outerHTML = content;
  });

  // Serialize the document back to a string and return it
  return doc.documentElement.innerHTML;
}

const mouseStyle = {
  "font-size": "14px",
  width: "25px",
  height: "25px",
  "z-index": "1",
  position: "absolute",
};

const cursorHeight = ref(null);
const variableBackground = computed(() => {
  return cursorHeight.value > getHeight.value ? "#FFC800" : "#FFF";
});
const contentRef = ref(null);
watch(cursorHeight, (newVal) => {
  if (newVal > contentRef.value.getBoundingClientRect().height) return;

  if (
    newVal < contentRef.value.getBoundingClientRect().height &&
    newVal < getHeight.value
  ) {
    setCursor(null, null, {
      background: "#FFF",
      ...mouseStyle,
    });
  }
  if (newVal < contentRef.value.getBoundingClientRect().height && newVal < 80) {
    setCursor(null, null, {
      background: "#FFF",
      ...mouseStyle,
    });
  }

  if (
    newVal < contentRef.value.getBoundingClientRect().height &&
    newVal > getHeight.value
  ) {
    setCursor(null, null, {
      background: "#FFC800",
      ...mouseStyle,
    });
  }
});

const { date, title: optionalTitle } = translation;
const tags = translation?.eventTags?.edges;
const title =
  translation?.eventsConfig?.title || optionalTitle;
const imgSrc =
  translation?.eventsConfig?.featuredImage?.sourceUrl;

const { perex, content, gallery, organisers } =
  translation?.eventsConfig || {};
const { timeFrom: timeStart, timeEnd } =
  translation?.eventsConfig?.datesettings || {};
const { entrance, entranceMembers, entrancePublic, lector, organizer, place } =
  translation?.eventsConfig?.meta || {};

const {
  maxUsers,
  eventRegisterUrl,
  eventRegistrationMethod,
  currentlyRegistered,
} = data?.value?.eventBy.translation.eventsConfig.capacity || {};

const getImages = (gallery) => {
  let galleryImages = gallery?.map((item) => item.mediaItemUrl);
  return galleryImages;
};

const hasDatePassed = (dateToCheck) => {
  const now = new Date();
  const dateToCompare = new Date(dateToCheck);
  return now > dateToCompare;
};

const updateWidth = () => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", { duration: 0.1, ease: "power3" });
    yTo.value = gsap.quickTo("#cursor", "y", { duration: 0.1, ease: "power3" });
  }
};

const width = ref(null);
const xTo = ref(null);
const yTo = ref(null);

const heroContent = ref(null);
const heroHeight = ref(0);
const heroImgContent = ref(null);
const heroImgHeight = ref(0);

onMounted(() => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", { duration: 0.1, ease: "power3" });
    yTo.value = gsap.quickTo("#cursor", "y", { duration: 0.1, ease: "power3" });
  }

  window.addEventListener("resize", updateWidth);

  if (heroContent.value) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        heroHeight.value = entry.contentRect.height;
      }
    });

    resizeObserver.observe(heroContent.value);

    // Ukládáme observer do ref, abychom ho mohli později zrušit
    heroContent.__resizeObserver = resizeObserver;
  }
  if (heroImgContent.value) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        heroImgHeight.value = entry.contentRect.height;
      }
    });

    resizeObserver.observe(heroImgContent.value);

    // Ukládáme observer do ref, abychom ho mohli později zrušit
    heroImgContent.__resizeObserver = resizeObserver;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
  window.document.removeEventListener("click", cursorOnClick.value);

  if (heroContent.__resizeObserver) {
    heroContent.__resizeObserver.unobserve(heroContent.value);
  }
  if (heroImgContent.__resizeObserver) {
    heroImgContent.__resizeObserver.unobserve(heroImgContent.value);
  }
});

const cursor = ref("");
const cursorStyle = ref({});
const cursorOnClick = ref(() => {});
const mouseMove = (e) => {
  if (width.value > 1024) {
    xTo.value(e.pageX);
    yTo.value(e.pageY);

    cursorHeight.value = e.pageY;
  }
};

const getHeight = computed(() => {
  return heroHeight.value + 143 - heroImgHeight.value / 2;
});

// const getDate = (datestring) => {
//   const date = new Date(datestring);
//   return `${date.getDate() < 10 ? "0" : ""}${date.getDate()}.${
//     date.getMonth() + 1 < 10 ? "0" : ""
//   }${date.getMonth() + 1}.${date.getFullYear()}`;
// };

const getDate = (
	dateString,
	timeString = null,
	returnISOTime = false
) => {
	const date = new Date(dateString);

	if (returnISOTime && timeString) {
		const [hours, minutes] = timeString.split(":");

		date.setHours(
			parseInt(hours, 10),
			parseInt(minutes, 10),
			0,
			0
		);

		const pad = (value) => String(value).padStart(2, "0");

		const timeZone = "Europe/Prague";

		const formatter = new Intl.DateTimeFormat("en-US", {
			timeZone,
			timeZoneName: "longOffset",
		});

		const parts = formatter.formatToParts(date);

		const timeZoneName = parts.find(
			(part) => part.type === "timeZoneName"
		)?.value;

		const offset = timeZoneName?.replace("GMT", "") || "+00:00";

		return `${date.getFullYear()}-${pad(
			date.getMonth() + 1
		)}-${pad(date.getDate())}T${pad(
			date.getHours()
		)}:${pad(date.getMinutes())}:00${offset}`;
	}

	return `${date.getDate() < 10 ? "0" : ""}${date.getDate()}.${
		date.getMonth() + 1 < 10 ? "0" : ""
	}${date.getMonth() + 1}.${date.getFullYear()}`;
};

const setCursor = (value, onClick, style) => {
  cursor.value = value;
  if (cursorOnClick.value !== onClick) {
    window.document.removeEventListener("click", cursorOnClick.value);
    cursorOnClick.value = onClick;

    window.document.addEventListener("click", cursorOnClick.value);
  }
  if (cursorStyle.value !== style) {
    cursorStyle.value = style;
  }
};

if (title) {
  useHeadMeta({
    title: title,
    description: perex,
    url: route.fullPath,
    csUrl: csSlug ? `cs/akce/${csSlug}` : null,
    enUrl: enSlug ? `en/events/${enSlug}` : null,
    image: imgSrc,
    alt: "Opero",
    breadcrumbs: [
      {
        name: t("events.meta.title"),
        item: route.fullPath.split("/").slice(0, -1).join("/"),
      },
    ],
  }, [{
    "@id": "#event",
    "@type": "Event",
    "name": title,
    "startDate": getDate(date, timeStart, true),
    "endDate": getDate(date, timeEnd, true),
    "image": imgSrc,
    "location": {
      "@id": "#company",
    },
    "organizer": {
      "@id": "#company",
    },
  }]);

  // useHeadMeta({
  //   title: title,
  //   description: perex,
  //   url: `cs/akce/${route.params.id}`,
  //   image: imgSrc,
  // });
}

useHead({
	script: [
		{
			src: 'https://partners.goout.net/cz-prague/operocz.js',
			tagPosition: 'bodyClose',
		},
	],
});
</script>

<template>
  <div @mousemove="mouseMove">
    <div
      class="event-detail-header"
      :style="{ height: getHeight + 'px' }"
      @mouseenter="
        setCursor(null, null, {
          background: variableBackground,
          ...mouseStyle,
        })
      "
      @mouseleave="
        setCursor(null, null, {
          background: '#FFC800',
          ...mouseStyle,
        })
      "
    >
      <div class="navbar-wrapper">
        <Navbar :is-dark="true" :is-white="true" :has-en="hasEn" en-list-page="/en/events" :cs-slug="csSlug" :en-slug="enSlug" />
      </div>

      <div
        v-if="data"
        ref="contentRef"
        class="flex justify-center mt-28 lg:mt-40 relative z-40 overflow-hidden"
      >
        <div class="container px-6">
          <div ref="heroContent">
            <div class="flex flex-wrap gap-2 pb-6">
              <div
                v-for="(tag, index) in tags"
                :key="index"
                class="event-tag !bg-white"
              >
                {{ tag?.node?.name }}
              </div>
            </div>
            <h1 class="goout-event-header">{{ title }}</h1>
            <div class="flex pt-8 pb-4">
              <b class="pr-1">{{ date ? getDate(date) : "" }}</b>
              |
              {{ timeStart }}
              {{ timeEnd ? ` - ${timeEnd}` : "" }}
            </div>
            <div class="pb-12 text-2xl">
              <div v-if="perex" v-html="perex"></div>
            </div>
            <div class="flex justify-center w-full">
              <div class="event-detail-ftr-img">
                <img ref="heroImgContent" :src="imgSrc" />
                <div
                  class="absolute -right-12 top-0 flex flex-col gap-5 mt-12 lg:flex w-6"
                >
                  <Socials :title="title" />
                </div>
              </div>
            </div>
          </div>

          <EventHeader
            :date="date"
            :time-end="timeEnd"
            :time-start="timeStart"
            :place="place"
            :lector="lector"
            :organizer="organizer"
            :entrance="entrance"
            :entrance-public="entrancePublic"
            :entrance-members="entranceMembers"
          />

          <div class="mb-12 mt-12" v-if="!hasDatePassed(date)">
            <div class="event-buttons flex justify-center w-full">
              <a
                v-if="eventRegistrationMethod === 'url' || eventRegistrationMethod === 'url_form'"
                :href="eventRegisterUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="goout-sale-button bg-transparent hover:bg-[#1A1A1A] uppercase border border-[#1A1A1A] rounded-full hover:text-white mt-4 mb-4 px-4 py-2 !font-normal text-sm mr-2"
              >
                {{ $t("events.button.participate") }}
              </a>

              <button
                v-if="eventRegistrationMethod === 'form' || eventRegistrationMethod === 'url_form'"
                id="opero-club-form-trigger"
                @click="isModalOpen = true"
                class="bg-transparent hover:bg-[#1A1A1A] uppercase border border-[#1A1A1A] rounded-full hover:text-white mt-4 mb-4 px-4 py-2 !font-normal text-sm ml-2"
              >
                {{ $t("events.button.participate_member") }}
              </button>
            </div>
          </div>

          <div class="goout-sale-holder"></div>

          <ClientOnly>
            <div class="flex justify-center pt-12 pb-24 event-content wysiwyg">
              <div v-html="replaceBlockquotesAndRemoveStyles(content)"></div>
            </div>
          </ClientOnly>

          <EventLogos :organisers="organisers" v-if="organisers" />

          <EventGallery
            :gallery="gallery[0].galleryConfig.images"
            v-if="gallery"
          />
          <!-- event form // external btn -->
          <div class="mb-12 md:mb-24" v-if="!hasDatePassed(date)">
            <div class="event-buttons flex justify-center w-full">
              <a
                v-if="eventRegistrationMethod === 'url' || eventRegistrationMethod === 'url_form'"
                :href="eventRegisterUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="bg-transparent hover:bg-[#1A1A1A] uppercase border border-[#1A1A1A] rounded-full hover:text-white mt-4 mb-4 px-4 py-2 !font-normal text-sm"
              >
                {{ $t("events.button.participate") }}
              </a>

              <button
                v-if="eventRegistrationMethod === 'form' || eventRegistrationMethod === 'url_form'"
                id="opero-club-form-trigger"
                @click="isModalOpen = true"
                class="bg-transparent hover:bg-[#1A1A1A] uppercase border border-[#1A1A1A] rounded-full hover:text-white mt-4 mb-4 px-4 py-2 !font-normal text-sm ml-2"
              >
                {{ $t("events.button.participate_member") }}
              </button>
            </div>
            <div v-if="eventRegistrationMethod === 'form' || eventRegistrationMethod === 'url_form'">
              <Teleport to="body">
                <EventForm
                  :event-id="data.eventBy.translation.eventId"
                  :has-full-capacity="maxUsers - currentlyRegistered < 1"
                  :isOpen="isModalOpen"
                  @close="isModalOpen = false"
                />
              </Teleport>
            </div>
          </div>

          <div class="flex gap-5 mb-12 mt-12 lg:hidden">
            <Socials :title="title" />
          </div>
        </div>
      </div>
      <!-- <div v-if="gallery">
        <div class="">
          <ImageCarousel
            v-if="getImages(gallery)"
            id="firstSpaces"
            :images="getImages(gallery)"
            :current-image="currentImage"
            :change-testimonial="changeTestimonial"
            :set-cursor="setCursor"
          />
        </div>
        <div class="max-w-7xl mb-24">
          <BubblesNavigationPaper
            :height="50"
            :width="40 * gallery.length"
            :start-width="50"
            :testimonials="gallery"
            :change-testimonial="changeTestimonial"
            :current-selected="currentImage"
          />
        </div>
      </div> -->
      <BecomeMember :set-cursor="setCursor" />
      <Footer :set-cursor="setCursor" />
    </div>
    <div class="hmhmhm"></div>

    <div>
      <div
        id="cursor"
        class="hidden pointer-events-none cursor-auto lg:cursor-none lg:flex"
        :style="cursorStyle"
        :class="{
          smallCursor: cursor === null,
          hidden: !width || width < 1024,
        }"
        v-html="cursor"
      ></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h1 {
  line-height: normal;
}
.container {
  width: 100vw;
  max-width: 840px;
}
.event-detail-header {
  background-color: $color-primary;
  height: 100vh;

  p {
    padding: 2rem 0;
    max-width: 600px;
  }
}

.event-detail-ftr-img {
  position: relative;

  img {
    width: 1200px;
    max-width: 1200px;
  }

  @media screen and (max-width: 1300px) {
    img {
      width: 900px;
      max-width: 900px;
    }
  }
  @media screen and (max-width: 1000px) {
    img {
      width: 100%;
      max-width: 100%;
    }
  }
}

.event-tag {
  background: $color-primary;
  border-radius: 100px;
  padding: 10px 20px;
  font-weight: 500;
  font-size: 16px;
  font-weight: 400;
  height: 40px;
  white-space: nowrap;

  display: inline-flex;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

#cursor {
  position: absolute;
  top: 0;
  left: -10px;
  width: 25px;
  height: 25px;
  font-weight: 400;

  justify-content: center;
  align-items: center;
  background-color: $color-primary;
  border-radius: 50%;
  z-index: 1000;
  cursor: none;
  transition:
    width 0.3s,
    height 0.3s,
    background-color 0.3s;
  transform: translate(-50%, -50%) translate(-150px, -150px);
  //pointer-events: none;
  text-align: center;
  display: none;
  @media screen {
    @media (pointer: fine) {
      display: flex;
    }
  }
}

.smallCursor {
  width: 20px;
  height: 20px;
  background-color: $color-primary;
}

.negative-margin {
  @media screen and (max-width: 450px) {
    margin-left: -7px;
  }
  //margin-top: -9px;
  //margin-bottom: -9px;
}
</style>

<style lang="scss">
.event-buttons {
  @media all and (max-width: 480px) {
    flex-direction: column;
    gap: 15px;

    a {
      text-align: center;
    }

    a, button {
      margin: 0;
    }
  }
}

.event-content {
  h2 {
    margin-bottom: 20px;
  }

  a {
    text-decoration: underline;
  }

  h3 {
    font-size: 28px !important;
    font-weight: 300;
    margin-bottom: 20px;
  }

  p,
  div {
    font-size: 20px !important;
  }

  ul {
    font-size: 20px;
    margin-left: 20px;
    list-style-type: disc;
    list-style-position: outside;

    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 1em;
  }
  ol {
    font-size: 20px;
    list-style-type: decimal;
    list-style-position: outside;

    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 1em;
  }
}
</style>
